/**
 * RoamerProfile.ts — WorkRoamer Roamer Intelligence Engine™ (Engine #2)
 *
 * The user-preference model that drives every recommendation and score.
 * "What is best for THIS user in THIS city right now?" is answered by combining
 * this profile with the current base (from the Roamer Location Engine™) and
 * live/placeholder data providers.
 *
 * DESIGN NOTES:
 *   • These fields may not be collected by any UI yet — this is a placeholder,
 *     future-ready model. Every field has a safe neutral default so the engine
 *     produces sensible output before onboarding exists.
 *   • Current Base + GPS are NOT stored here. They are owned by the Location
 *     Engine (single source of truth) and read at scoring time.
 *   • Pure data — no React, no fetch, no side effects.
 */

export type TravelStyle = 'solo' | 'couple' | 'family' | 'group';

export type ClimatePreference = 'tropical' | 'temperate' | 'cool' | 'dry' | 'any';

/**
 * A 0–100 "importance" slider. 0 = don't care, 50 = neutral, 100 = critical.
 * Used for soft lifestyle preferences that weight scoring rather than gate it.
 */
export type Importance = number;

export interface RoamerProfile {
  version: 1;

  // ── Budget & stay ───────────────────────────────────────────────────────────
  /** Total monthly budget in USD (null = unknown). */
  monthlyBudgetUsd: number | null;
  /** Housing-only monthly budget in USD (null = unknown). */
  housingBudgetUsd: number | null;
  /** Intended length of stay in days (null = unknown). */
  lengthOfStayDays: number | null;

  // ── Who is travelling ────────────────────────────────────────────────────────
  remoteWorker: boolean;
  digitalNomad: boolean;
  travelStyle: TravelStyle;

  // ── Work needs ───────────────────────────────────────────────────────────────
  /** Desired minimum internet speed in Mbps (null = unknown). */
  preferredInternetMbps: number | null;
  needsQuietWorkspace: boolean;
  needsCoworking: boolean;

  // ── Lifestyle preferences (soft weights, 0–100) ──────────────────────────────
  nightlife: Importance;
  beach: Importance;
  nature: Importance;
  walkability: Importance;
  publicTransit: Importance;
  safetyPriority: Importance;

  // ── Climate & logistics ──────────────────────────────────────────────────────
  climate: ClimatePreference;
  petFriendly: boolean;
  /** Free-form for now (e.g. "tourist", "visa-exempt", "work-permit"). */
  visaStatus: string | null;
  /** ISO-ish language preference (e.g. "en", "es"); null = unknown. */
  preferredLanguage: string | null;
}

/** Safe, neutral defaults. Tuned for the app's core audience (remote nomads). */
export const DEFAULT_ROAMER_PROFILE: RoamerProfile = {
  version: 1,

  monthlyBudgetUsd: null,
  housingBudgetUsd: null,
  lengthOfStayDays: null,

  remoteWorker: true,
  digitalNomad: true,
  travelStyle: 'solo',

  preferredInternetMbps: null,
  needsQuietWorkspace: false,
  needsCoworking: false,

  nightlife: 50,
  beach: 50,
  nature: 50,
  walkability: 50,
  publicTransit: 50,
  safetyPriority: 60,

  climate: 'any',
  petFriendly: false,
  visaStatus: null,
  preferredLanguage: null,
};

/**
 * Stable signature of a profile's material fields — used as a cache key so the
 * engine can memoise recommendations and invalidate them when preferences move.
 */
export function profileSignature(p: RoamerProfile): string {
  return [
    p.monthlyBudgetUsd, p.housingBudgetUsd, p.lengthOfStayDays,
    p.remoteWorker ? 1 : 0, p.digitalNomad ? 1 : 0, p.travelStyle,
    p.preferredInternetMbps, p.needsQuietWorkspace ? 1 : 0, p.needsCoworking ? 1 : 0,
    p.nightlife, p.beach, p.nature, p.walkability, p.publicTransit, p.safetyPriority,
    p.climate, p.petFriendly ? 1 : 0, p.visaStatus ?? '', p.preferredLanguage ?? '',
  ].join('|');
}

/** Merge a partial patch onto a base profile, keeping the version pinned. */
export function mergeProfile(base: RoamerProfile, patch: Partial<RoamerProfile>): RoamerProfile {
  return { ...base, ...patch, version: 1 };
}
